import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  Complaint,
  ConsentForm,
  Customer,
  MedicalRecord,
  PhotoItem,
  PhotoSession,
  Staff,
  Treatment,
} from '@/lib/types';
import {
  Banner,
  Card,
  PageHeader,
  Pill,
  Tag,
  cellStyle,
  fieldStyle,
  thStyle,
  useToast,
} from '@/components/ui';
import { Select } from '@/components/Select';
import { CustomerPicker } from '@/components/CustomerPicker';
import { SignaturePad } from '@/components/SignaturePad';
import { useSort, SortTh } from '@/components/sortable';

const SCOPES = ['clinical', 'before_after', 'marketing_anonymous', 'marketing_identified', 'academic', 'data_pool'];
const SCOPE_LABEL: Record<string, string> = {
  clinical: '臨床',
  before_after: '前後對比',
  marketing_anonymous: '行銷(去識別)',
  marketing_identified: '行銷(具名)',
  academic: '學術',
  data_pool: '資料池',
};
const COMPLAINT_LABEL: Record<string, string> = {
  INVESTIGATING: '調查中',
  NEGOTIATING: '協商中',
  RESOLVED: '已結案',
};

export function CompliancePage() {
  const qc = useQueryClient();
  const { msg, ok, fail } = useToast();
  const [customerId, setCustomerId] = useState('');
  const [scopes, setScopes] = useState<string[]>(['clinical']);
  const [treatmentId, setTreatmentId] = useState('');
  const [templateCode, setTemplateCode] = useState('');
  const [signerName, setSignerName] = useState('');
  const [signatureData, setSignatureData] = useState('');
  const [sigKey, setSigKey] = useState(0); // 重置簽名板
  const [contra, setContra] = useState('');
  const [cStatus, setCStatus] = useState('');
  const [issue, setIssue] = useState('');
  const [sessionId, setSessionId] = useState('');
  // #2 針劑批號
  const [injItems, setInjItems] = useState<{ itemName: string; lot: string; qty: number }[]>([{ itemName: '', lot: '', qty: 1 }]);
  const [injNote, setInjNote] = useState('');
  // #3 術前審查
  const PREOP_ITEMS = ['同意書已簽署', '無禁忌症 / 過敏', '生命徵象正常', '已說明風險與術後照護'];
  const [preChecked, setPreChecked] = useState<boolean[]>([false, false, false, false]);
  const [preAnesthesia, setPreAnesthesia] = useState('none');
  // #6c SOAP 病歷
  const [soap, setSoap] = useState({ s: '', o: '', a: '', p: '' });
  const [soapSig, setSoapSig] = useState('');
  const [soapSigKey, setSoapSigKey] = useState(0);

  const customers = useQuery({ queryKey: ['customers'], queryFn: () => api.get<Customer[]>('/customers') });
  const treatments = useQuery({ queryKey: ['treatments'], queryFn: () => api.get<Treatment[]>('/catalog/treatments') });
  const consents = useQuery({ queryKey: ['consents', customerId], enabled: !!customerId, queryFn: () => api.get<ConsentForm[]>(`/compliance/consents?customerId=${customerId}`) });
  const templates = useQuery({ queryKey: ['consent-templates'], queryFn: () => api.get<{ code: string; name: string; category: string; summary: string }[]>('/compliance/consent-templates') });
  const injections = useQuery({ queryKey: ['injections', customerId], enabled: !!customerId, queryFn: () => api.get<any[]>(`/compliance/injection-records/${customerId}`) });
  const preops = useQuery({ queryKey: ['preops', customerId], enabled: !!customerId, queryFn: () => api.get<any[]>(`/compliance/preop-reviews/${customerId}`) });
  const notes = useQuery({ queryKey: ['notes', customerId], enabled: !!customerId, queryFn: () => api.get<any[]>(`/compliance/treatment-notes/${customerId}`) });
  const record = useQuery({ queryKey: ['mrec', customerId], enabled: !!customerId, queryFn: () => api.get<MedicalRecord | null>(`/compliance/medical-records/${customerId}`) });
  const complaints = useQuery({ queryKey: ['complaints'], queryFn: () => api.get<Complaint[]>('/compliance/complaints') });
  const staff = useQuery({ queryKey: ['staff'], queryFn: () => api.get<Staff[]>('/catalog/staff') });
  const isOverdue = (c: Complaint) => !!c.dueAt && c.status !== 'RESOLVED' && new Date(c.dueAt).getTime() < Date.now();
  const sessions = useQuery({ queryKey: ['photo-sessions', customerId], enabled: !!customerId, queryFn: () => api.get<PhotoSession[]>(`/compliance/photo-sessions?customerId=${customerId}`) });
  const photos = useQuery({ queryKey: ['photos', sessionId], enabled: !!sessionId, queryFn: () => api.get<PhotoItem[]>(`/compliance/photo-sessions/${sessionId}/photos`) });
  const verify = useQuery({ queryKey: ['verify', sessionId], enabled: !!sessionId, queryFn: () => api.get<{ valid: boolean }>(`/compliance/photo-sessions/${sessionId}/verify`) });

  const nameOf = (id: string) => customers.data?.find((c) => c.id === id)?.name ?? id;
  const toggleScope = (s: string) => setScopes((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  const sortConsents = useSort(consents.data ?? [], {
    version: (c) => c.version,
    scopes: (c) => c.scopes.map((s) => SCOPE_LABEL[s] ?? s).join('、'),
    signedAt: (c) => c.signedAt,
  });
  const sortPhotos = useSort(photos.data ?? [], {
    phase: (p) => (p.phase === 'before' ? '術前' : '術後'),
    angle: (p) => p.angle,
    prevHash: (p) => p.prevHash ?? '',
    hash: (p) => p.hash,
  });
  const shownComplaints = (complaints.data ?? []).filter((c) => !cStatus || c.status === cStatus);
  const sortComplaints = useSort(shownComplaints, {
    customer: (c) => nameOf(c.customerId),
    issue: (c) => c.issue,
    level: (c) => c.level,
    status: (c) => COMPLAINT_LABEL[c.status],
  });

  const addConsent = useMutation({
    mutationFn: () => api.post('/compliance/consents', {
      customerId, treatmentId, scopes,
      templateCode: templateCode || undefined,
      signerName: signerName || undefined,
      signatureData: signatureData || undefined,
    }),
    onSuccess: () => {
      ok('同意書已簽署存證');
      setSignatureData(''); setSignerName(''); setSigKey((k) => k + 1);
      void qc.invalidateQueries({ queryKey: ['consents', customerId] });
    },
    onError: fail,
  });
  const saveRecord = useMutation({
    mutationFn: () => api.post('/compliance/medical-records', { customerId, contraindications: contra.split(/[,，、\s]+/).filter(Boolean) }),
    onSuccess: () => { ok('病歷已更新'); void qc.invalidateQueries({ queryKey: ['mrec', customerId] }); },
    onError: fail,
  });
  const addComplaint = useMutation({
    mutationFn: () => api.post('/compliance/complaints', { customerId, issue, level: 2 }),
    onSuccess: () => { ok('客訴已建立'); setIssue(''); void qc.invalidateQueries({ queryKey: ['complaints'] }); },
    onError: fail,
  });
  const assignComplaint = useMutation({
    mutationFn: (v: { id: string; ownerStaffId: string }) => api.patch(`/compliance/complaints/${v.id}/assign`, { ownerStaffId: v.ownerStaffId }),
    onSuccess: () => { ok('已指派負責人'); void qc.invalidateQueries({ queryKey: ['complaints'] }); },
    onError: fail,
  });
  const stepComplaint = useMutation({
    mutationFn: (v: { id: string; verb: 'advance' | 'regress' }) => api.post(`/compliance/complaints/${v.id}/${v.verb}`),
    onSuccess: () => { ok('客訴狀態已更新'); void qc.invalidateQueries({ queryKey: ['complaints'] }); },
    onError: fail,
  });
  const newSession = useMutation({
    mutationFn: () => api.post<PhotoSession>('/compliance/photo-sessions', { customerId, treatmentId, consentScopes: scopes }),
    onSuccess: (s) => { ok('影像紀錄已建立'); setSessionId(s.id); void qc.invalidateQueries({ queryKey: ['photo-sessions', customerId] }); },
    onError: fail,
  });
  const addPhoto = useMutation({
    mutationFn: (phase: 'before' | 'after') => api.post('/compliance/photos', { sessionId, angle: 'front', phase, url: `${phase}-${Date.now()}.jpg` }),
    onSuccess: () => { ok('影像已串鏈'); void qc.invalidateQueries({ queryKey: ['photos', sessionId] }); void qc.invalidateQueries({ queryKey: ['verify', sessionId] }); },
    onError: fail,
  });
  const addInjection = useMutation({
    mutationFn: () => api.post('/compliance/injection-records', { customerId, treatmentId, items: injItems.filter((i) => i.itemName && i.lot), witnessNote: injNote }),
    onSuccess: () => { ok('針劑批號已存證'); setInjItems([{ itemName: '', lot: '', qty: 1 }]); setInjNote(''); void qc.invalidateQueries({ queryKey: ['injections', customerId] }); },
    onError: fail,
  });
  const addPreop = useMutation({
    mutationFn: () => api.post('/compliance/preop-reviews', { customerId, treatmentId, checklist: PREOP_ITEMS.map((item, i) => ({ item, checked: preChecked[i] })), anesthesia: preAnesthesia }),
    onSuccess: () => { ok('術前審查已建立'); setPreChecked([false, false, false, false]); void qc.invalidateQueries({ queryKey: ['preops', customerId] }); },
    onError: fail,
  });
  const addNote = useMutation({
    mutationFn: () => api.post('/compliance/treatment-notes', { customerId, treatmentId, subjective: soap.s, objective: soap.o, assessment: soap.a, plan: soap.p, signatureData: soapSig || undefined }),
    onSuccess: () => { ok('病歷已紀錄'); setSoap({ s: '', o: '', a: '', p: '' }); setSoapSig(''); setSoapSigKey((k) => k + 1); void qc.invalidateQueries({ queryKey: ['notes', customerId] }); },
    onError: fail,
  });
  const approveMarketing = useMutation({
    mutationFn: (id: string) => api.post(`/compliance/photo-sessions/${id}/approve-marketing`),
    onSuccess: () => { ok('已核准對外行銷用途'); void qc.invalidateQueries({ queryKey: ['photo-sessions', customerId] }); },
    onError: fail,
  });

  return (
    <div>
      <PageHeader title="合規 / 病歷 / 影像" subtitle="同意書版本控管；禁忌症安全把關；客訴流程；影像 SHA-256 防竄改雜湊鏈 + 同意範圍授權" />
      <Banner msg={msg} />

      <Card title="選擇顧客">
        <div style={{ maxWidth: 320 }}>
          <CustomerPicker value={customerId} onChange={(id) => { setCustomerId(id); setSessionId(''); }} />
        </div>
      </Card>

      {customerId && (
        <>
          <Card title="知情同意書(數位簽署)">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
              <Select
                value={treatmentId}
                onChange={(val) => setTreatmentId(val)}
                placeholder="— 療程 —"
                options={(treatments.data ?? []).map((t) => ({ value: t.id, label: `${t.name}` }))}
              />
              <Select
                value={templateCode}
                onChange={setTemplateCode}
                placeholder="衛福部範本…"
                options={[
                  { value: '', label: '不套用範本' },
                  ...(templates.data ?? []).map((t) => ({ value: t.code, label: t.name })),
                ]}
              />
            </div>
            {templateCode && (
              <div style={{ fontSize: 11, color: 'var(--warn)', background: 'var(--warn-bg)', padding: '6px 10px', borderRadius: 6, marginBottom: 8 }}>
                ⚠ 說明書重點:{templates.data?.find((t) => t.code === templateCode)?.summary}
              </div>
            )}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
              {SCOPES.map((s) => (
                <label key={s} style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}>
                  <input type="checkbox" checked={scopes.includes(s)} onChange={() => toggleScope(s)} />
                  {SCOPE_LABEL[s]}
                </label>
              ))}
              <input style={{ ...fieldStyle, width: 160 }} placeholder="簽署人姓名(本人/法代)" value={signerName} onChange={(e) => setSignerName(e.target.value)} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 4 }}>病患 / 法定代理人簽名</div>
              <SignaturePad key={sigKey} onChange={setSignatureData} />
            </div>
            <button
              className="btn btn-primary"
              disabled={!treatmentId || scopes.length === 0 || !signatureData || addConsent.isPending}
              onClick={() => addConsent.mutate()}
            >
              簽署存證
            </button>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 14 }}>
              <thead><tr><SortTh label="版本" sortKey="version" sort={sortConsents} /><th style={thStyle}>範本</th><SortTh label="授權範圍" sortKey="scopes" sort={sortConsents} /><th style={thStyle}>簽署</th><SortTh label="時間" sortKey="signedAt" sort={sortConsents} /></tr></thead>
              <tbody>
                {sortConsents.sorted.map((c) => (
                  <tr key={c.id}>
                    <td style={cellStyle} className="num">v{c.version}</td>
                    <td style={cellStyle} title={c.templateName ?? ''}>{c.templateName ? (c.templateName.length > 14 ? c.templateName.slice(0, 14) + '…' : c.templateName) : '—'}</td>
                    <td style={cellStyle}>{c.scopes.map((s) => SCOPE_LABEL[s] ?? s).join('、')}</td>
                    <td style={cellStyle}>{c.signatureData ? <Tag tone="success">已簽名{c.signerName ? `·${c.signerName}` : ''}</Tag> : <Tag tone="default">未簽</Tag>}</td>
                    <td style={cellStyle} className="num">{c.signedAt.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <Card title="病歷 / 禁忌症" right={<button className="btn btn-ghost" disabled={saveRecord.isPending} onClick={() => saveRecord.mutate()}>儲存</button>}>
            <input style={{ ...fieldStyle, width: '100%' }} placeholder="逗號分隔，如：抗凝血劑, 蟹足腫" defaultValue={(record.data?.contraindications ?? []).join(', ')} onChange={(e) => setContra(e.target.value)} />
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ink-3)' }}>目前禁忌：{(record.data?.contraindications ?? []).join('、') || '無'}</div>
          </Card>

          {/* #3 術前審查(特管列管術式) */}
          <Card title="特管術前審查">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
              {PREOP_ITEMS.map((item, i) => (
                <label key={item} style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="checkbox" checked={preChecked[i]} onChange={() => setPreChecked((c) => c.map((v, j) => j === i ? !v : v))} />{item}
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>麻醉</span>
              <Select value={preAnesthesia} onChange={setPreAnesthesia} style={{ width: 130 }} options={[{ value: 'none', label: '無 / 表麻' }, { value: 'local', label: '局部麻醉' }, { value: 'sedation', label: '重度鎮靜' }, { value: 'general', label: '全身麻醉' }]} />
              <button className="btn btn-primary" disabled={!treatmentId || addPreop.isPending} onClick={() => addPreop.mutate()}>送出審查</button>
            </div>
            {!!preops.data?.length && (
              <div style={{ marginTop: 8, fontSize: 12 }}>最近審查:{preops.data[0].passed ? <Tag tone="success">通過</Tag> : <Tag tone="warn">未通過</Tag>}（{preops.data[0].createdAt.slice(0, 10)}）</div>
            )}
          </Card>

          {/* #2 針劑批號綁定 + 拆封見證 */}
          <Card title="針劑批號 / 拆封見證">
            {injItems.map((it, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <input style={{ ...fieldStyle, flex: 2 }} placeholder="品名(如:玻尿酸/肉毒)" value={it.itemName} onChange={(e) => setInjItems((s) => s.map((x, j) => j === i ? { ...x, itemName: e.target.value } : x))} />
                <input style={{ ...fieldStyle, flex: 2 }} placeholder="批號 lot" value={it.lot} onChange={(e) => setInjItems((s) => s.map((x, j) => j === i ? { ...x, lot: e.target.value } : x))} />
                <input type="number" style={{ ...fieldStyle, width: 60 }} value={it.qty} onChange={(e) => setInjItems((s) => s.map((x, j) => j === i ? { ...x, qty: Number(e.target.value) } : x))} />
              </div>
            ))}
            <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => setInjItems((s) => [...s, { itemName: '', lot: '', qty: 1 }])}>+ 加一支</button>
            <input style={{ ...fieldStyle, width: '100%', marginTop: 6 }} placeholder="拆封見證(如:客人在場核對衛署字號)" value={injNote} onChange={(e) => setInjNote(e.target.value)} />
            <button className="btn btn-primary" style={{ marginTop: 8 }} disabled={!treatmentId || !injItems.some((i) => i.itemName && i.lot) || addInjection.isPending} onClick={() => addInjection.mutate()}>存證</button>
            {!!injections.data?.length && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ink-3)' }}>已存證 {injections.data.length} 筆,最近批號:{injections.data[0].items?.map((x: any) => x.lot).join('、')}</div>}
          </Card>

          {/* #6c 結構化病歷 SOAP + 醫師數位簽章 */}
          <Card title="臨床病歷 SOAP（醫師簽章）">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <input style={fieldStyle} placeholder="S 主訴" value={soap.s} onChange={(e) => setSoap((p) => ({ ...p, s: e.target.value }))} />
              <input style={fieldStyle} placeholder="O 客觀檢查" value={soap.o} onChange={(e) => setSoap((p) => ({ ...p, o: e.target.value }))} />
              <input style={fieldStyle} placeholder="A 評估" value={soap.a} onChange={(e) => setSoap((p) => ({ ...p, a: e.target.value }))} />
              <input style={fieldStyle} placeholder="P 計畫 / 醫囑" value={soap.p} onChange={(e) => setSoap((p) => ({ ...p, p: e.target.value }))} />
            </div>
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 4 }}>醫師數位簽章(簽署後不可否認)</div>
              <SignaturePad key={soapSigKey} height={110} onChange={setSoapSig} />
            </div>
            <button className="btn btn-primary" style={{ marginTop: 8 }} disabled={!(soap.s || soap.o || soap.a || soap.p) || addNote.isPending} onClick={() => addNote.mutate()}>儲存病歷</button>
            {!!notes.data?.length && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ink-3)' }}>已紀錄 {notes.data.length} 筆{notes.data[0].signedAt ? '(已簽章)' : ''}</div>}
          </Card>

          <Card title="影像紀錄（雜湊鏈）" right={<button className="btn btn-ghost" disabled={!treatmentId || newSession.isPending} onClick={() => newSession.mutate()}>建立影像組</button>}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
              <Select
                value={sessionId}
                onChange={(val) => setSessionId(val)}
                placeholder="— 選擇影像組 —"
                options={(sessions.data ?? []).map((s) => ({ value: s.id, label: `${s.id.slice(0, 8)} · ${s.createdAt.slice(0, 10)}` }))}
              />
              {sessionId && <>
                <button className="btn btn-ghost" onClick={() => addPhoto.mutate('before')}>＋術前</button>
                <button className="btn btn-ghost" onClick={() => addPhoto.mutate('after')}>＋術後</button>
                {verify.data && (verify.data.valid
                  ? <Pill bg="var(--success-bg)" color="var(--success)">雜湊鏈完整</Pill>
                  : <Pill bg="var(--danger-bg)" color="var(--danger)">鏈結損毀</Pill>)}
                <button className="btn btn-ghost" title="醫療法§85:前後對比照不得作廣告" disabled={approveMarketing.isPending} onClick={() => approveMarketing.mutate(sessionId)}>核准對外行銷</button>
              </>}
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-4)', marginBottom: 6 }}>※ 醫療法§85:前後對比影像不得作為廣告;僅「行銷同意」之影像可核准對外。</div>
            {sessionId && (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><SortTh label="階段" sortKey="phase" sort={sortPhotos} /><SortTh label="角度" sortKey="angle" sort={sortPhotos} /><SortTh label="prevHash" sortKey="prevHash" sort={sortPhotos} /><SortTh label="hash" sortKey="hash" sort={sortPhotos} /></tr></thead>
                <tbody>
                  {sortPhotos.sorted.map((p) => (
                    <tr key={p.id}>
                      <td style={cellStyle}>{p.phase === 'before' ? '術前' : '術後'}</td>
                      <td style={cellStyle}>{p.angle}</td>
                      <td style={cellStyle} className="num" title={p.prevHash}>{p.prevHash ? p.prevHash.slice(0, 10) + '…' : '(genesis)'}</td>
                      <td style={cellStyle} className="num" title={p.hash}>{p.hash.slice(0, 10)}…</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}

      <Card
        title="客訴管理"
        right={
          <div style={{ width: 150 }}>
            <Select value={cStatus} onChange={setCStatus} placeholder="全部狀態" options={[{ value: '', label: '全部狀態' }, { value: 'INVESTIGATING', label: '調查中' }, { value: 'NEGOTIATING', label: '協商中' }, { value: 'RESOLVED', label: '已結案' }]} />
          </div>
        }
      >
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input style={{ ...fieldStyle, flex: 1 }} placeholder="客訴內容" value={issue} onChange={(e) => setIssue(e.target.value)} />
          <button className="btn btn-primary" disabled={!customerId || !issue || addComplaint.isPending} onClick={() => addComplaint.mutate()}>建立客訴</button>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><SortTh label="顧客" sortKey="customer" sort={sortComplaints} /><SortTh label="內容" sortKey="issue" sort={sortComplaints} /><SortTh label="等級" sortKey="level" sort={sortComplaints} /><th style={thStyle}>負責人</th><th style={thStyle}>SLA 期限</th><SortTh label="狀態" sortKey="status" sort={sortComplaints} /><th style={thStyle}>操作</th></tr></thead>
          <tbody>
            {sortComplaints.sorted.map((c) => (
              <tr key={c.id} style={isOverdue(c) ? { background: 'var(--danger-bg)' } : undefined}>
                <td style={cellStyle}>{nameOf(c.customerId)}</td>
                <td style={cellStyle}>{c.issue}</td>
                <td style={cellStyle} className="num">L{c.level}</td>
                <td style={cellStyle}>
                  <div style={{ width: 120 }}>
                    <Select value={c.ownerStaffId ?? ''} onChange={(v) => assignComplaint.mutate({ id: c.id, ownerStaffId: v })} placeholder="未指派" options={(staff.data ?? []).map((s) => ({ value: s.id, label: s.name }))} />
                  </div>
                </td>
                <td style={cellStyle} className="num">
                  {c.dueAt ? c.dueAt.slice(0, 10) : '—'}
                  {isOverdue(c) && <Pill bg="var(--danger-bg)" color="var(--danger)" style={{ marginLeft: 6 }}>逾期</Pill>}
                </td>
                <td style={cellStyle}><Pill bg="var(--primary-tint)" color="var(--primary-deep)">{COMPLAINT_LABEL[c.status]}</Pill></td>
                <td style={{ ...cellStyle, display: 'flex', gap: 6 }}>
                  <button className="btn btn-ghost" disabled={c.status === 'RESOLVED'} onClick={() => stepComplaint.mutate({ id: c.id, verb: 'advance' })}>推進</button>
                  <button className="btn btn-ghost" disabled={c.status === 'INVESTIGATING'} onClick={() => stepComplaint.mutate({ id: c.id, verb: 'regress' })}>退回</button>
                </td>
              </tr>
            ))}
            {complaints.data?.length === 0 && <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={7}>尚無客訴</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
